/**
 * LES CHOIX DEPENDANTS : un champ a choix dont chaque option appartient a une
 * valeur d'un autre champ du meme jeu (le Job « Devis » appartient au Pole
 * `sales`).
 *
 * LA DECLARATION, DEUX CLEFS ET RIEN D'AUTRE : `dependDe` sur le champ (la clef
 * du parent, un `select` du meme jeu), `parent` sur chaque option (une valeur
 * du parent). Une option sans `parent` vaut pour tous les parents.
 *
 * CE QUE LE SOCLE EN FAIT, partout avec ces fonctions : la saisie ne propose
 * que les options du parent de la fiche, changer de parent vide la valeur
 * devenue incompatible (dans le meme patch), l'ecriture d'une valeur
 * incompatible est refusee (client, Functions, outils MCP), et un kanban ou un
 * filtre dont la porte fixe le parent ne montre que ses options.
 *
 * LIMITE VOULUE : un parent ne depend pas lui-meme d'un autre champ. Une
 * chaine obligerait a vider en cascade, et aucun besoin ne la demande.
 */

/** Un champ, reduit a ce que la dependance lit. */
export interface ChampAChoix {
  key: string;
  type: string;
  dependDe?: string | undefined;
  options?: readonly { value: string; parent?: string | undefined; label?: string; archived?: boolean | undefined }[] | undefined;
  label?: string;
}

const estAChoix = (champ: ChampAChoix): boolean => champ.type === 'select' || champ.type === 'multiselect';

/** Les fautes d'un jeu de champs, une phrase chacune. Vide : la declaration tient. */
export function fautesDeDependance(champs: readonly ChampAChoix[]): string[] {
  const fautes: string[] = [];
  for (const champ of champs) {
    if (champ.dependDe === undefined) continue;
    const parent = champs.find((c) => c.key === champ.dependDe);
    if (champ.dependDe === champ.key) { fautes.push(`le champ "${champ.key}" ne depend pas de lui-meme`); continue; }
    if (parent === undefined) { fautes.push(`le champ "${champ.key}" depend de "${champ.dependDe}", absent du jeu`); continue; }
    if (parent.type !== 'select') { fautes.push(`le champ "${champ.key}" depend de "${parent.key}" : un parent est un choix simple`); continue; }
    if (parent.dependDe !== undefined) { fautes.push(`le champ "${parent.key}" est le parent de "${champ.key}" : il ne depend pas lui-meme d'un autre`); continue; }
    if (!estAChoix(champ)) continue;
    const valeurs = new Set((parent.options ?? []).map((o) => o.value));
    for (const option of champ.options ?? []) {
      if (option.parent !== undefined && !valeurs.has(option.parent)) {
        fautes.push(`l'option "${option.value}" de "${champ.key}" appartient a "${option.parent}", absent des choix de "${parent.key}"`);
      }
    }
  }
  return fautes;
}

/** La valeur du parent, ramenee a une clef (vide : `null`). */
function clefDuParent(valeur: unknown): string | null {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  return String(valeur);
}

/** L'option vaut-elle pour ce parent ? Sans parent sur la fiche, seules les options communes. */
export function optionPourParent(option: { parent?: string | undefined }, valeurDuParent: unknown): boolean {
  return option.parent === undefined || option.parent === clefDuParent(valeurDuParent);
}

/** Les options d'un champ que ce parent permet. Un champ sans `dependDe` les garde toutes. */
export function optionsPourParent<O extends { parent?: string | undefined }>(
  champ: { dependDe?: string | undefined; options?: readonly O[] | undefined },
  valeurDuParent: unknown,
): O[] {
  const options = [...(champ.options ?? [])];
  return champ.dependDe === undefined ? options : options.filter((o) => optionPourParent(o, valeurDuParent));
}

/** Les clefs d'une valeur de choix (une liste pour un choix multiple), sans les vides. */
function clefsDe(valeur: unknown): string[] {
  if (valeur === null || valeur === undefined || valeur === '') return [];
  if (Array.isArray(valeur)) return valeur.filter((v) => v !== null && v !== undefined && v !== '').map(String);
  return [String(valeur)];
}

/** Une valeur portee par un champ dependant, qui n'appartient pas au parent de la fiche. */
export interface ChoixIncompatible {
  champ: string;
  valeur: string;
  parent: string;
  valeurDuParent: string | null;
  /** La phrase a montrer, avec les libelles declares. */
  phrase: string;
}

function libelle(champ: ChampAChoix | undefined, valeur: string | null): string {
  if (valeur === null) return 'vide';
  return champ?.options?.find((o) => o.value === valeur)?.label ?? valeur;
}

/**
 * LES VALEURS INCOMPATIBLES d'une fiche, lues par `lire(clef)`. Une valeur
 * inconnue du catalogue n'est pas jugee ici : d'autres refus la portent.
 */
export function choixIncompatibles(champs: readonly ChampAChoix[], lire: (clef: string) => unknown): ChoixIncompatible[] {
  const sortie: ChoixIncompatible[] = [];
  for (const champ of champs) {
    if (champ.dependDe === undefined || !estAChoix(champ)) continue;
    const parent = champs.find((c) => c.key === champ.dependDe);
    const valeurDuParent = clefDuParent(lire(champ.dependDe));
    for (const valeur of clefsDe(lire(champ.key))) {
      const option = champ.options?.find((o) => o.value === valeur);
      if (option === undefined || optionPourParent(option, valeurDuParent)) continue;
      sortie.push({
        champ: champ.key,
        valeur,
        parent: champ.dependDe,
        valeurDuParent,
        phrase: `« ${libelle(champ, valeur)} » n'appartient pas a ${parent?.label ?? champ.dependDe} « ${libelle(parent, valeurDuParent)} ».`,
      });
    }
  }
  return sortie;
}

/**
 * CE QU'UN CHANGEMENT DE PARENT VIDE : les champs qui dependent de `clef` et
 * dont la valeur actuelle n'appartient plus au nouveau parent. Une liste (choix
 * multiple) garde ses clefs compatibles. Rend le complement du patch :
 * `{ job: null }`, ou `{}` quand rien ne bouge.
 */
export function viderLesDependants(
  champs: readonly ChampAChoix[],
  clef: string,
  nouvelleValeur: unknown,
  lire: (clef: string) => unknown,
): Record<string, string[] | null> {
  const patch: Record<string, string[] | null> = {};
  for (const champ of champs) {
    if (champ.dependDe !== clef || !estAChoix(champ)) continue;
    const actuelles = clefsDe(lire(champ.key));
    if (actuelles.length === 0) continue;
    const gardees = actuelles.filter((valeur) => {
      const option = champ.options?.find((o) => o.value === valeur);
      return option === undefined || optionPourParent(option, nouvelleValeur);
    });
    if (gardees.length === actuelles.length) continue;
    patch[champ.key] = champ.type === 'multiselect' ? gardees : null;
  }
  return patch;
}
